import React, { useContext, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { GeoProvider } from "./context/GeoContext";

import ProtectedRoute from "./routes/ProtectedRoute";
import LocaleLayout from "./routes/LocaleLayout";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "./i18n/i18n";

import WhatsAppFab from "./components/WhatsAppFab/WhatsAppFab";
import Profile from "./pages/Profile/Profile";
import AffiliationPage from "./pages/Affiliation/Affiliation";
import CartPage from "./pages/Cart/Cart";
import Home from "./pages/home/home";
import Offers from "./pages/Offers/Offers";
import FAQPage from "./pages/faq/FAQ";
import ContactPage from "./pages/contact/contact";
import TermsPage from "./pages/Static/TermsPage";
import PrivacyPage from "./pages/Static/PrivacyPage";
import AdminSupportChats from "./pages/SupportChats/SupportChats";

import PartnerOffer from "./pages/PartnerOffers/PartnerOffers";
import ConfigPage from "./pages/ConfigPage/ConfigPage";
import AdminUsers from "./pages/AdminPages/AdminUsers";
import AdminTransactions from "./pages/AdminPages/AdminTransactions";
import AdminNotifications from "./pages/AdminPages/AdminNotifications";
import AdminPromocodes from "./pages/AdminPages/AdminPromocodes";
import AdminCarriers from "./pages/AdminPages/AdminCarriers";
import AdminWallets from "./pages/AdminPages/AdminWallets";
import AdminRoles from "./pages/AdminPages/AdminRoles";
import NavigationHeader from "./components/Elements/Header/Header";
import EsimScreen from "./pages/Esim/Esim";
import EsimStats from "./pages/EsimStats/EsimStats";
import Login from "./pages/Authentication/Authentication";
import Register from "./pages/Authentication/Register";
import ForgotPassword from "./pages/Authentication/ForgotPassword";
import Footer from "./components/Elements/Footer/Footer";
import { initGA } from "./analytics/analytics";
import AnalyticsTracker from "./analytics/AnalyticsTracker";

function RootRedirect() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language && SUPPORTED_LANGUAGES.includes(i18n.language) ? i18n.language : DEFAULT_LANGUAGE;
  return <Navigate to={`/${currentLang}`} replace />;
}

function FallbackRedirect() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const currentLang = i18n.language && SUPPORTED_LANGUAGES.includes(i18n.language) ? i18n.language : DEFAULT_LANGUAGE;

  if (
    location.pathname.startsWith("/admin") ||
    location.pathname === "/config" ||
    location.pathname === "/partner-offer"
  ) {
    return null;
  }

  const segments = location.pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return <Navigate to={`/${currentLang}`} replace />;
  }

  const VALID_SUBROUTES = [
    "shop",
    "sign-in",
    "register",
    "forgot-password",
    "esim",
    "cart",
    "faq",
    "contact",
    "terms",
    "privacy-policy",
    "affiliation",
    "account",
  ];

  // If path already starts with a supported language or contains repeated language tags
  if (SUPPORTED_LANGUAGES.includes(segments[0])) {
    const targetSubroute = segments
      .slice()
      .reverse()
      .find((seg) => VALID_SUBROUTES.includes(seg));
    if (targetSubroute) {
      return <Navigate to={`/${currentLang}/${targetSubroute}${location.search}${location.hash}`} replace />;
    }
    return <Navigate to={`/${currentLang}`} replace />;
  }

  // Path without language prefix (e.g., /shop -> /en/shop)
  if (VALID_SUBROUTES.includes(segments[0])) {
    return <Navigate to={`/${currentLang}/${segments.join("/")}${location.search}${location.hash}`} replace />;
  }
  // Fallback for any other unmatched path to avoid infinite loop
  return <Navigate to={`/${currentLang}`} replace />;
}

function App() {
  useEffect(() => {
    initGA();
  }, []);

  return (
    <BrowserRouter>
      <AnalyticsTracker />
      <AuthProvider>
        <GeoProvider>
          <div className="main-container">
            <CartProvider>
              <NavigationHeader />
              <Routes>
                {/* Admin / Non-localized routes */}
                <Route
                  path="/partner-offer"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <PartnerOffer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/config"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <ConfigPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminUsers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/transactions"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminTransactions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/wallets"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminWallets />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/carriers"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminCarriers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/roles"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminRoles />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/promocodes"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminPromocodes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/support-chats"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminSupportChats />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/notifications"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminNotifications />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/esim"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <EsimStats />
                    </ProtectedRoute>
                  }
                />

                {/* Root redirect */}
                <Route path="/" element={<RootRedirect />} />
                {/* Localized routes top-level subfolder prefix /:lng */}
                <Route path="/:lng" element={<LocaleLayout />}>
                  <Route index element={<Home />} />
                  <Route path="shop" element={<Offers />} />
                  <Route path="sign-in" element={<Login />} />
                  <Route path="register" element={<Register />} />
                  <Route path="forgot-password" element={<ForgotPassword />} />
                  <Route
                    path="esim"
                    element={
                      <ProtectedRoute>
                        <EsimScreen />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="cart" element={<CartPage />} />
                  <Route path="faq" element={<FAQPage />} />
                  <Route path="contact" element={<ContactPage />} />
                  <Route path="terms" element={<TermsPage />} />
                  <Route path="privacy-policy" element={<PrivacyPage />} />
                  <Route
                    path="affiliation"
                    element={
                      <ProtectedRoute>
                        <AffiliationPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="account"
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* Fallback for paths without language prefix e.g. /shop -> /en/shop */}
                <Route path="*" element={<FallbackRedirect />} />
              </Routes>
            </CartProvider>
          </div>
        </GeoProvider>
        <Footer />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
